import * as Raven from 'raven-js'
import * as inRange from 'lodash/inRange'
import { ErrorState, UnexpectedErrorType, UnexpectedInternalError } from 'interfaces/errors'
import { AjaxError } from 'rxjs/ajax'
import * as moment from 'moment'
import { Subject } from 'rxjs'
import { Fetchable } from 'interfaces/fetchable'
import { ABORTED_STATUS_CODE } from 'constants/ajax'
import resolveErrorById, { DEFAULT_NON_FIELD_ERROR, NON_FIELD_ERRORS } from 'constants/errors'
import resolveErrorByCode from 'constants/errors-http'
import { FieldErrors, FieldValues } from 'react-hook-form'
import { capitalizeFirstLetter } from 'helpers/formatHelpers'

export const UNEXPECTED_ENDPOINT_STATUS = `Unexpected endpoint status`
export const NO_PROVIDERS_ACCOUNTS_AVAILABLE = 'No providers accounts available'
export const BAD_TIMEPERIOD_INSIGHTS = `Insights with bad or without time period detected`
export const BAD_CAGEGORY_INSIGHTS = `Insights with missed category detected`
export const UNRECOGNIZED_TUCI_ERROR = `Unrecognized TUCI error code`
export const PROPERTY_ASSESSMENT_FAILED = `Property assessment failed`
export const NO_FORM_HANDLER_FOUND = `No form handler found`
export const NO_FORM_URL_FOUND = `No form url for POST request found`
const TIMED_OUT_COMPONENT = `Component's data load has timed out`
export const FORGOTTEN_GUARD = `Some guard has been forgotten`
export const BAD_COMPOSE_STATE = `Bad compose state`
export const BACKEND_COMPOSE_VALIDATION = `Bad compose state`

export function constructError (errorType: UnexpectedErrorType, internalError?: Error): ErrorState {
  return {
    errorType,
    internalError,
  }
}

export function isAjaxError (error: UnexpectedInternalError): error is AjaxError {
  return Boolean((error as AjaxError).xhr)
}

export function isNetworkError (error: UnexpectedInternalError): boolean {
  return isAjaxError(error) && error.status === ABORTED_STATUS_CODE
}

export function getErrorMessage (error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return String(error)
}

export const getErrorType = (error: UnexpectedInternalError): UnexpectedErrorType => {
  if (isAjaxError(error)) {
    if (error.status === 404) {
      return 'page-not-found'
    }

    return inRange(error.status, 500, 600)
      ? 'server-error'
      : 'request-error'
  }

  return 'runtime-error'
}

const SENTRY_URL = __SENTRY_URL__
const IS_SENTRY_ENABLED = SENTRY_URL !== null

const ignoreErrors = [
  /^No error$/,
  /^Unexpected end of input$/,
]

export const onBreadcrumb$: Subject<any> = new Subject()

export function initializeRaven () {
  if (!IS_SENTRY_ENABLED) {
    return
  }

  Raven.config(SENTRY_URL, {
    release: __VERSION__,
    ignoreErrors,
  }).install()

  Raven.setBreadcrumbCallback((crumb: any) => {
    onBreadcrumb$.next(crumb)
  })
}

export function captureException (error: Error) {
  // tslint:disable-next-line:no-console
  console.error(error)
  IS_SENTRY_ENABLED && Raven.captureException(error)
}

export function captureWarning (warning: string, extra?: any) {
  extra ? console.warn(warning, extra) : console.warn(warning)
  if (IS_SENTRY_ENABLED) {
    const options: Record<string, any> = {
      level: 'warning',
    }
    if (extra) {
      options.extra = extra
    }
    Raven.captureMessage(warning, options)
  }
}

export function captureTimedOutComponent (moduleName: string, guardName: string, timeoutMs: number) {
  const timeout = `${ moment.duration(timeoutMs).asSeconds() }s`
  const extra = { moduleName, guardName, timeout }

  captureWarning(TIMED_OUT_COMPONENT, extra)
}

export function captureForgottenGuard (moduleName: string, guardName: string) {
  const extra = { moduleName, guardName }

  captureWarning(FORGOTTEN_GUARD, extra)
}

export function captureEndpointError ({ status, xhr, request }: AjaxError) {
  const endpointUrl = request
    ? request.url
    : (xhr ? xhr.responseURL : `Not detected`)

  const statusString = `${ status }${ xhr ? ': ' + xhr.statusText : '' }`

  const responsePayload = xhr && xhr.response ? xhr.response : null
  return captureWarning(UNEXPECTED_ENDPOINT_STATUS, {
    endpointUrl,
    status: statusString,
    responsePayload,
  })
}

export const createForgottenGuardError = (containerName: string, firstGuardName: string, ...otherGuardNames: string[]) => {
  const guardNames = [firstGuardName, ...otherGuardNames]
  return new Error(`${ containerName } should be covered by guards: ${ guardNames.join(', ') }`)
}

export const createGuardOrderError = (shouldBefore: string, shouldAfterFirst: string, ...shouldAfterOthers: string[]) => {
  const shouldAfter = [shouldAfterFirst, ...shouldAfterOthers]
  return new Error(`Guard ${ shouldBefore } should be placed after: ${ shouldAfter.join(', ') }`)
}

export const getRequestErrors = (request: Fetchable<null>): string[] => {
  const { isLoading, errorCode, errorsMap } = request

  if (isLoading || !errorCode || !errorsMap) {
    return []
  }

  // Check for non-field Errors
  if (!!errorsMap['non_field_errors']) {
    return errorsMap['non_field_errors']
  }

  // Check for field errors
  const newArray = Object.keys(errorsMap).map((key) => capitalizeFirstLetter(key) + ': ' + errorsMap[key])

  if (newArray.length > 0) {
    return newArray
  }

  // Check for HTTP code error & fallback to default
  return [resolveErrorByCode(errorCode) || resolveErrorById(DEFAULT_NON_FIELD_ERROR)]
}

export const fetchFieldErrors = (errors: FieldErrors) => {
  const errorKeys = Object.keys(errors)
  const errorsMap = errorKeys.reduce((acc, key) => {
    const error = errors[key]
    const errorMessage = error && error.message
    return {
      ...acc,
      ...errorMessage ? { [key]: errorMessage } : {},
    }
  }, {})

  return errorsMap
}

export const collateFieldErrors = (errors: FieldValues) => {
  const errorKeys = Object.keys(errors)
  if (!errorKeys || !errorKeys.length) {
    return
  }

  const formatErrorKey = (key: string) => key === NON_FIELD_ERRORS ? '' : capitalizeFirstLetter(
    key.replaceAll('_', ' ') + ': '
  )

  return errorKeys.map((key) => formatErrorKey(key) + errors[key])
}

